// Backlog 072-B — `grid follow <trace_id>` verb.
//
// Re-attaches to an in-flight (or just-finished) deploy trace and renders the
// same multi-step progress + final summary that a synchronous `grid plug`
// would have shown. Hits the same `GET /deploys/:trace_id` polling endpoint;
// no new API surface required.
//
// Exit semantics (per kickoff): success → 0, failure → 1. Network/timeout
// errors surface as `CloudGridError` and the top-level handler maps them to
// exit 1 too.

import { Command } from 'commander';

import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { handleV2Error } from '../handle-error.js';
import { CloudGridError } from '../utils.js';
import { isTraceId } from '../services/trace-format.js';
import { pollDeploy, type DeployTrace } from '../services/plug-poll.js';
import { MultiStepRenderer } from '../render/multi-step.js';
import {
  hasStructuredDeployError,
  renderStructuredDeployError,
} from '../render/deploy-error.js';
import { formatElapsedHuman } from '../services/deploys-log.js';

export interface FollowOpts {
  json?: boolean;
  /**
   * Backlog 187 — append the technical detail (raw error text +
   * `failed_step`) to the failure surface. Default surface is just the
   * user-language message + suggested fixes.
   */
  verbose?: boolean;
}

export interface FollowDeps {
  client?: IV2Client;
  writer?: (s: string) => void;
  tty?: boolean;
  pollIntervalMs?: number;
  maxDurationMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Inner runner — exported for unit tests. Does not call `process.exit`;
 * throws `CloudGridError` on a failed deploy so the top-level handler can
 * map it to a non-zero exit.
 */
export async function runFollow(
  traceId: string,
  opts: FollowOpts,
  deps: FollowDeps = {},
): Promise<void> {
  if (!isTraceId(traceId)) {
    throw new CloudGridError(
      `'${traceId}' is not a valid trace id. Expected the form d_<entity>_<suffix>.`,
    );
  }

  const client = deps.client ?? (await getV2Client());
  const writer = deps.writer ?? ((s: string) => process.stdout.write(s));

  // ── --json: one-shot final state, no chrome. ──
  if (opts.json) {
    const final = await pollDeploy(client, traceId, {
      pollIntervalMs: deps.pollIntervalMs,
      maxDurationMs: deps.maxDurationMs,
      sleep: deps.sleep,
    });
    writer(JSON.stringify(serializeForJson(final)) + '\n');
    if (final.status !== 'success') {
      throw new CloudGridError(
        pickFollowErrorMessage(final.error) ??
          `Deploy failed (${final.error?.code ?? 'UNKNOWN'}).`,
      );
    }
    return;
  }

  // ── Human mode: re-attach via the multi-step renderer. ──
  const renderer = new MultiStepRenderer({
    tty: deps.tty,
    writer: deps.writer,
  });
  // First poll lets us pull the entity name for the header. The poll loop
  // takes over from there.
  const firstSnapshot = await client.get<DeployTrace>(`/deploys/${traceId}`);
  const entityLabel = firstSnapshot.entity?.name ?? traceId;
  renderer.start(`↻ Following ${entityLabel} (trace ${traceId})...`);
  renderer.update(firstSnapshot.steps);

  // If the first snapshot was already terminal, skip polling and finish.
  let final: DeployTrace;
  if (firstSnapshot.status === 'success' || firstSnapshot.status === 'failed') {
    final = firstSnapshot;
  } else {
    try {
      final = await pollDeploy(client, traceId, {
        pollIntervalMs: deps.pollIntervalMs,
        maxDurationMs: deps.maxDurationMs,
        sleep: deps.sleep,
        onPoll: (trace) => renderer.update(trace.steps),
      });
    } catch (err) {
      renderer.finish();
      throw err;
    }
  }

  const elapsed = computeElapsedMs(final);
  if (final.status === 'success') {
    const outlet = final.result?.url ?? '';
    const head = `✓ Live in ${formatElapsedHuman(elapsed)}.`;
    renderer.finish(outlet ? `${head}\n  Outlet: ${outlet}` : head);
    return;
  }

  // Failed.
  renderer.finish();

  // Backlog 187 — structured error rendering. When the API returns the
  // backlog-187 shape, surface the user-language message + suggested
  // fixes inline. `--verbose` adds the technical detail. Falls through
  // to the legacy single-line error string for pre-187 traces.
  if (hasStructuredDeployError(final) && final.error) {
    writer(
      renderStructuredDeployError(final.error, {
        entitySlug: final.entity?.name ?? entityLabel,
        verbose: opts.verbose === true,
      }) + '\n',
    );
  }

  throw new CloudGridError(
    pickFollowErrorMessage(final.error) ??
      `Deploy failed (${final.error?.code ?? 'UNKNOWN'}).`,
  );
}

/**
 * Backlog 187 — mirror of plug.ts's `pickErrorMessage`. Prefers the
 * user-language `message_user` from the structured shape; falls back to
 * the legacy `.message` field which the route shim still populates.
 */
function pickFollowErrorMessage(
  err: { message_user?: string; message?: string } | null | undefined,
): string | undefined {
  if (!err) return undefined;
  if (typeof err.message_user === 'string' && err.message_user.length > 0) {
    return err.message_user;
  }
  if (typeof err.message === 'string' && err.message.length > 0) {
    return err.message;
  }
  return undefined;
}

function computeElapsedMs(trace: DeployTrace): number {
  if (trace.started_at === null) return 0;
  const start = Date.parse(trace.started_at);
  if (!Number.isFinite(start)) return 0;
  const end =
    trace.completed_at !== null ? Date.parse(trace.completed_at) : Date.now();
  if (!Number.isFinite(end)) return 0;
  return Math.max(0, end - start);
}

function serializeForJson(trace: DeployTrace): Record<string, unknown> {
  return {
    trace_id: trace.trace_id,
    status: trace.status,
    entity: trace.entity,
    started_at: trace.started_at,
    completed_at: trace.completed_at,
    steps: trace.steps,
    error: trace.error,
    result: trace.result,
  };
}

export const followCommand = new Command('follow')
  .description('Re-attach to a deploy trace and stream progress until done')
  .argument('<trace_id>', 'Deploy trace id (e.g. d_abc123_xyz789)')
  .option('--json', 'Machine-readable output (final trace JSON, no chrome)')
  .option(
    '--verbose',
    'On deploy failure, also print the underlying technical detail. Default surface is the user-language message + suggested next steps.',
  )
  .action(async (traceId: string, opts: FollowOpts) => {
    try {
      await runFollow(traceId, opts);
    } catch (err) {
      await handleV2Error(err, { json: !!opts.json });
      // handleV2Error calls process.exit; this throw is unreachable but
      // keeps TS happy if the type ever drifts.
      throw err;
    }
  });
